import { Box, FormHelperText, TextField } from '@mui/material';
import { FormDatepickerField, FormNumberField, FormSelect, FormSelectOption, FormTextField } from 'components/forms';
import { useEffect } from 'react';
import { Control, DeepMap, FieldError, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { COMPETITOR_OPTIONS } from 'shared/constants';
import { useCompetitorProductOptions } from 'shared/hooks';
import { formatFourDecimalMoney } from 'shared/utility';
import { CompetitorProductDropdown, CurrentPricesData } from 'types/backend';
import { EditModelCurrentPricesForm } from 'types/forms';

interface EditMarketPriceFormProps {
    row?: CurrentPricesData;
    control: Control<EditModelCurrentPricesForm>;
    errors: DeepMap<EditModelCurrentPricesForm, FieldError> | undefined;
    watch?: UseFormWatch<EditModelCurrentPricesForm>;
    setValue?: UseFormSetValue<EditModelCurrentPricesForm>;
}

export function EditMarketPriceForm({ control, errors, watch, setValue, row }: EditMarketPriceFormProps) {
    const reasonForChange = watch ? watch('reason') : '';
    const competitorOption = watch ? watch('competitor') : '';
    const isCompetitorRequired =
        reasonForChange === 'Competition' ? { required: 'Competitor is required' } : { required: '' };
    const isCompetitorPriceRequired =
        reasonForChange === 'Competition' ? { required: 'Competitor Price is required' } : { required: '' };
    const competitorProductRequiredRule =
        reasonForChange === 'Competition' ? { required: 'Competitor Product is required' } : { required: '' };
    const isEstAnnualVolumeRequired = {
        required: ['Competition', 'Volume Growth'].includes(reasonForChange)
            ? 'Estimated Annual Volume is required'
            : '',
    };

    const {
        data: competitorProductDropdownResponse,
        isLoading: competitorProductsIsLoading,
        isFetching: competitorProductsIsFetching,
    } = useCompetitorProductOptions(competitorOption);

    const competitorProductDropdownOptions: FormSelectOption<CompetitorProductDropdown>[] =
        competitorProductDropdownResponse?.map((competitorProduct) => {
            return {
                key: competitorProduct.competitorProductsId,
                value: competitorProduct.competitorProduct,
                displayText: competitorProduct.competitorProduct,
                original: competitorProduct,
            };
        }) || [];

    const isCompetitorProductDropdownLoading = competitorProductsIsLoading || competitorProductsIsFetching;

    const today = new Date().toLocaleDateString();
    const endOfTime = new Date('12/31/9999').toLocaleDateString();
    useEffect(() => {
        if (setValue) {
            setValue('start', today);
            setValue('end', endOfTime);
        }
    }, [setValue, today, endOfTime]);

    return (
        <>
            <Box sx={{ paddingTop: '20px' }}>
                <TextField
                    label='Tier 1 Target Price'
                    disabled
                    defaultValue={formatFourDecimalMoney(Number(row?.tierOnePrice))}
                    fullWidth
                />
            </Box>
            <Box sx={{ paddingTop: '20px' }}>
                <TextField
                    label='Customer Tier Target'
                    disabled
                    defaultValue={formatFourDecimalMoney(Number(row?.targetPrice))}
                    fullWidth
                />
            </Box>
            <Box sx={{ paddingTop: '20px' }}>
                <TextField
                    label='Current Price'
                    disabled
                    defaultValue={formatFourDecimalMoney(Number(row?.price))}
                    fullWidth
                />
            </Box>
            <Box sx={{ paddingTop: '20px' }}>
                <FormNumberField
                    name={'newPrice'}
                    control={control}
                    label='New Price'
                    type='money'
                    rules={{
                        required: 'New Price is required',
                        min: { value: 0, message: 'New Price cannot be negative' },
                        max: { value: 5, message: 'New Price cannot exceed $5.00' },
                    }}
                    fieldError={errors?.newPrice}
                />
            </Box>
            <Box sx={{ paddingTop: '20px' }}>
                <FormDatepickerField
                    name={'start'}
                    control={control}
                    label='Start Date'
                    rules={{ required: 'Start date is required' }}
                    fieldError={errors?.start}
                    disabled={true}
                />
            </Box>
            <Box sx={{ paddingTop: '20px' }}>
                <FormDatepickerField
                    name={'end'}
                    control={control}
                    label='Effective End'
                    rules={{ required: 'End date is required' }}
                    fieldError={errors?.end}
                />
            </Box>
            <Box sx={{ paddingTop: '20px' }}>
                <FormSelect
                    name={'reason'}
                    label='Reason For Change'
                    control={control}
                    rules={{ required: 'Reason for change is required' }}
                    options={[
                        { value: 'Volume Growth', displayText: 'Volume Growth' },
                        { value: 'Competition', displayText: 'Competition' },
                        { value: 'Other', displayText: 'Other' },
                    ]}
                    fieldError={errors?.reason}
                />
                <FormHelperText error sx={{ paddingLeft: '15px' }}>
                    {errors?.reason?.message}
                </FormHelperText>
            </Box>
            <Box sx={{ paddingTop: '20px' }}>
                <FormTextField name={'reasonNotes'} label={'Reason For Change Notes'} control={control} />
            </Box>
            <Box sx={{ paddingTop: '20px' }}>
                <FormSelect
                    name={'competitor'}
                    label='Competitor'
                    control={control}
                    rules={isCompetitorRequired}
                    options={COMPETITOR_OPTIONS}
                    fieldError={errors?.competitor}
                />
                <FormHelperText error> {errors?.competitor?.message}</FormHelperText>
            </Box>
            <Box sx={{ paddingTop: '20px' }}>
                <FormNumberField
                    name={'competitorPrice'}
                    control={control}
                    label='Competitor Price'
                    rules={isCompetitorPriceRequired}
                    type='money'
                    fieldError={errors?.competitorPrice}
                />
            </Box>
            <Box sx={{ paddingTop: '20px' }}>
                <FormSelect
                    name={'competitorProduct'}
                    label='Competitor Product'
                    control={control}
                    options={competitorProductDropdownOptions}
                    isLoading={isCompetitorProductDropdownLoading}
                    rules={competitorProductRequiredRule}
                    watch={watch}
                    fieldError={errors?.competitorProduct}
                />
                <FormHelperText error>{errors?.competitorProduct?.message}</FormHelperText>
            </Box>
            <Box sx={{ paddingTop: '20px' }}>
                <FormNumberField
                    name={'estAnnualRevenue'}
                    control={control}
                    label='Estimated Annual Volume'
                    type='number'
                    rules={isEstAnnualVolumeRequired}
                    fieldError={errors?.estAnnualRevenue}
                />
            </Box>
        </>
    );
}
